import { useQuery } from '@tanstack/react-query';
import { useDebounce } from './useDebounce';
import type { SearchResponse, SearchOptions } from '@/types/search';

const API_BASE_URL = 'http://localhost:3000/api';

/**
 * Fetch search results from the API
 */
async function fetchSearchResults(
    query: string,
    options: SearchOptions = {}
): Promise<SearchResponse> {
    const { type = 'all', limit = 5 } = options;

    const params = new URLSearchParams({
        q: query,
        type,
        limit: limit.toString(),
    });

    const response = await fetch(`${API_BASE_URL}/search?${params}`);

    if (!response.ok) {
        throw new Error('Search request failed');
    }

    return response.json();
}

/**
 * Custom hook for global search with debouncing and caching
 * 
 * @param query - Search query string
 * @param options - Search options (type, limit, enabled)
 * @returns React Query result with search data
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useGlobalSearch(searchQuery, {
 *   type: 'all',
 *   limit: 5,
 * });
 * ```
 */
export function useGlobalSearch(query: string, options: SearchOptions = {}) {
    const { type = 'all', limit = 5, enabled = true } = options;

    // Debounce the query to avoid excessive API calls
    const debouncedQuery = useDebounce(query, 300);

    return useQuery({
        queryKey: ['global-search', debouncedQuery, type, limit],
        queryFn: () => fetchSearchResults(debouncedQuery, { type, limit }),
        enabled: enabled && debouncedQuery.length >= 2, // Only search if query is 2+ chars
        staleTime: 30000, // 30 seconds
        gcTime: 300000, // 5 minutes (formerly cacheTime)
        retry: 1, // Only retry once on failure
    });
}

/**
 * Hook for searching products only
 */
export function useProductSearch(query: string, limit: number = 10) {
    return useGlobalSearch(query, { type: 'products', limit });
}

/**
 * Hook for searching orders only
 */
export function useOrderSearch(query: string, limit: number = 10) {
    return useGlobalSearch(query, { type: 'orders', limit });
}

/**
 * Hook for searching customers only
 */
export function useCustomerSearch(query: string, limit: number = 10) {
    return useGlobalSearch(query, { type: 'customers', limit });
}
